import { inject, Injectable } from '@angular/core';
import { Nullable } from 'primeng/ts-helpers';
import { map, Observable, switchMap, tap } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import { LOCAL_STORAGE_FILTER_KEYS } from '../../../../shared/filters/constants/local-storage-filter-keys.constant';
import { FilterType } from '../../../../shared/filters/contract/filter-type.contract';
import { CommonFilterView } from '../../../../shared/filters/contract/filter-view/common-filter-view.contract';
import { OrganisationFilterView } from '../../../../shared/filters/contract/filter-view/organisation-filter-view.contract';
import { UpdateFilterCommand } from '../../../../shared/filters/contract/update-filter-command.contract';
import { FilterTypes } from '../../../../shared/filters/enums/filter-types.enum';
import { SingleFilterOption } from '../../../../shared/filters/enums/single-filter-option.enum';
import { FiltersBaseService } from '../../../../shared/filters/filters-base.service';
import { BaseFilterViewConverter } from '../../../../shared/filters/model/filter-converters/base-filter-view-converter.class';
import { FormattedFilterStorage } from '../../../../shared/filters/model/formatted-filter-storage.class';
import { FiltersLocalStorageService } from '../../../../shared/filters/services/filters-local-storage.service';
import { FilterConverterFactoryService } from '../../../../shared/services/filter-converter-factory.service';
import { StockType } from '../../stocks/enums/stock-type.enum';
import { StockTypeResolverService } from '../../stocks/services/stock-type-resolver.service';
import { OrganisationApiService } from '../../../organisations/services/organisation-api.service';
import { LocationTypeDisplayPipe } from '../../../resources/equipment/components/equipments-details/components/events/pipes/location-type-display.pipe';
import { ViewOrganisation } from '../../../resources/equipment/contract/view-organisation.contract';
import { ProjectType } from '../../projects/enums/project-type.enum';
import { ProjectTypeResolverService } from '../../projects/services/project-type-resolver.service';
import { LocationsApiService } from '../../services/locations-api.service';
import { ActiveLocationsFilterCollectionView } from '../contract/active-locations-filter-collection-view.contract';
import { ActiveLocationsFilterParams } from '../contract/active-locations-filter-params.class';
import { ActiveLocationsFilterRequestParams } from '../contract/active-locations-filter-request-params.contract';
import { ActiveLocationFilterType } from '../enums/active-location-filter-type.enum';

@Injectable({ providedIn: 'root' })
export class ActiveLocationsFilterService extends FiltersBaseService<ActiveLocationsFilterRequestParams> {
  private readonly authService = inject(AuthService);
  private readonly locationsApiService = inject(LocationsApiService);
  private readonly organisationApiService = inject(OrganisationApiService);
  private readonly converterFactory = inject(FilterConverterFactoryService);
  private readonly filtersLocalStorageService = inject(FiltersLocalStorageService);
  private readonly locationTypeDisplayPipe = inject(LocationTypeDisplayPipe);
  private readonly stockTypeResolverService = inject(StockTypeResolverService);
  private readonly projectTypeResolverService = inject(ProjectTypeResolverService);

  private organisationTree: ViewOrganisation[] = [];
  private formattedFilterStorage: FormattedFilterStorage | Nullable;
  private isInitialized = false;

  constructor() {
    super(new ActiveLocationsFilterParams());
  }

  public override init(commands?: UpdateFilterCommand[]): void {
    if (!this.isInitialized) {
      this.initDefaultFilters(this.filtersLocalStorageService, commands);
      this.isInitialized = true;
      return;
    }
    if (commands && commands.length > 0) {
      this.resetFilters(commands, true, true);
      return;
    }
    // Service is provided in root, so subsequent init() calls (e.g. when re-entering
    // a consuming page) must not rebind local-storage backing. Just refresh filter
    // options for the current params so the consumer sees up-to-date data.
    this.updateFilters();
  }

  override getFilterParams(): ActiveLocationsFilterRequestParams {
    return super.getFilterParams();
  }

  override updateFilterParams(commands: UpdateFilterCommand[], updateFilters = true, checkConsistency = false): void {
    super.updateFilterParams(commands, updateFilters, checkConsistency);
  }

  override updateFilters(): void {
    this.getFiltersWithOrgTree(this.getFilterParams()).subscribe((filters) => this.mergeToResultFilters(filters));
  }

  override getFilters(params: ActiveLocationsFilterRequestParams): Observable<FilterType[]> {
    return this.getFilterCollection(params).pipe(map((collection) => this.convertToFilterTypes(collection)));
  }

  override getAllFilters(): Observable<FilterType[]> {
    return this.getFiltersWithOrgTree(new ActiveLocationsFilterParams().getFilterParams());
  }

  private getFiltersWithOrgTree(params: ActiveLocationsFilterRequestParams): Observable<FilterType[]> {
    return this.getOrganisationTree().pipe(
      tap((organisationTree) => (this.organisationTree = organisationTree)),
      switchMap(() => this.getFilters(params)),
    );
  }

  override resetFilters(
    defaultFilters: UpdateFilterCommand[] = [],
    updateFilters = true,
    checkConsistency = false,
  ): void {
    super.resetFilters(defaultFilters, updateFilters, checkConsistency);
  }

  private getFilterCollection(
    params: ActiveLocationsFilterRequestParams,
  ): Observable<ActiveLocationsFilterCollectionView> {
    return this.locationsApiService.getActiveLocationsFilters(params).pipe(
      map((collection) => ({
        ...collection,
        [ActiveLocationFilterType.MATCH_ALL_LABELS]: [{ name: SingleFilterOption.ALL_LABELS, count: null }],
      })),
    );
  }

  private getOrganisationTree(): Observable<ViewOrganisation[]> {
    return this.organisationApiService.getOrganisationsByCustomerId(this.authService.userCustomerId, true);
  }

  private initDefaultFilters(
    filtersLocalStorageService: FiltersLocalStorageService,
    overriddenDefaultCommands?: UpdateFilterCommand[],
  ): void {
    this.formattedFilterStorage = new FormattedFilterStorage(
      LOCAL_STORAGE_FILTER_KEYS.activeLocationsFilters,
      filtersLocalStorageService,
      this,
      this.destroyRef,
      overriddenDefaultCommands,
    );
  }

  private convertToFilterTypes(collection: ActiveLocationsFilterCollectionView): FilterType[] {
    const filters = (Object.keys(collection) as Array<keyof ActiveLocationsFilterCollectionView>)
      .map((key) =>
        this.createConverter(
          key as ActiveLocationFilterType,
          (collection[key] ?? []) as Array<CommonFilterView | OrganisationFilterView>,
        ),
      )
      .map((filterConverter) => filterConverter?.getFilters())
      .filter(Boolean);

    return this.composeFilters(filters, FilterTypes.LABEL, FilterTypes.MATCH_ALL_LABELS);
  }

  private createConverter(
    type: ActiveLocationFilterType,
    filters: Array<CommonFilterView | OrganisationFilterView>,
  ): BaseFilterViewConverter<unknown> {
    switch (type) {
      case ActiveLocationFilterType.TYPE:
        return this.converterFactory.getCommonConverter(
          filters as CommonFilterView[],
          this.params,
          FilterTypes.TYPE,
          this.locationTypeDisplayPipe,
        );
      case ActiveLocationFilterType.CATEGORY:
        return this.converterFactory.getCommonConverter(
          filters as CommonFilterView[],
          this.params,
          FilterTypes.CATEGORY,
          {
            transform: (value: string) => this.resolveCategoryDisplayName(value),
          },
        );
      case ActiveLocationFilterType.COST_CENTER:
        return this.converterFactory.getCommonConverter(
          filters as CommonFilterView[],
          this.params,
          FilterTypes.COST_CENTER,
        );
      case ActiveLocationFilterType.ORGANISATION:
        return this.converterFactory.getOrganisationConverter(
          filters as OrganisationFilterView[],
          this.params,
          this.organisationTree,
        );
      case ActiveLocationFilterType.LABEL:
        return this.converterFactory.getLabelConverter(filters as CommonFilterView[], this.params);
      case ActiveLocationFilterType.MATCH_ALL_LABELS:
        return this.converterFactory.getMatchAllLabelsConverter(filters as CommonFilterView[], this.params);
      default:
        throw new Error(`Unsupported filter type: ${type}`);
    }
  }

  private resolveCategoryDisplayName(value: string): string {
    const stockType = this.resolveStockType(value);
    if (stockType) {
      return this.stockTypeResolverService.resolveName(stockType);
    }

    const projectType = this.resolveProjectType(value);
    if (projectType) {
      return this.projectTypeResolverService.resolveName(projectType);
    }

    return value;
  }

  private resolveStockType(value: string): StockType | null {
    const normalizedValue = value.toUpperCase();
    return Object.values(StockType).find((stockType) => stockType.toUpperCase() === normalizedValue) ?? null;
  }

  private resolveProjectType(value: string): ProjectType | null {
    const normalizedValue = value.toUpperCase();
    return Object.values(ProjectType).find((projectType) => projectType.toUpperCase() === normalizedValue) ?? null;
  }
}
